import type { CrewPosition, WorkshopRecurrence } from '@bonsai/shared';

import { apiFetch } from '../lib/apiFetch';

// The seat DTOs live next door (they outgrew this file) but are re-exported here,
// so every existing `from '../api/workshops'` import keeps working.
import type {
  TreeReplacementDetail,
  TreeReplacementSource,
  TreeSpeciesOption,
  WorkshopTreeReplacement,
} from './tree-replacements/types';
import type { VenueCadence, WorkshopSeriesSummary } from './workshop-series.types';
import type { RosterBooking, WorkshopReservation, WorkshopRoster } from './workshopSeats.types';

export type { RosterBooking, WorkshopReservation, WorkshopRoster };
export type {
  TreeReplacementDetail,
  TreeReplacementSource,
  TreeSpeciesOption,
  WorkshopTreeReplacement,
};
export type { VenueCadence, WorkshopSeriesSummary };
// The venue Workshops-tab stats moved next door for the same reason; same re-export.
export {
  fetchVenueWorkshopStats,
  type FetchVenueWorkshopStatsParams,
  type VenueWorkshopStats,
} from './venue-workshop-stats';

// ─── Types — mirror backend/src/services/workshop.service.ts ──────────────────

/** A crew member as it appears in a resolved crew audit change (BON-201). */
export interface AuditCrewMember {
  /** Display-ready name in "First L." form, e.g. "Eden P." Falls back to email
   *  local-part or the raw key when the user can't be resolved. */
  name: string;
  position: 'teacher' | 'assistant' | 'teacher_in_training' | string;
}

/** The kind of workshop audit event, discriminating creation and lifecycle
 *  transitions from a plain field edit (BON-219). Mirrored from the backend
 *  `WorkshopAuditEventType` — keep in sync. */
export type WorkshopAuditEventType =
  | 'created'
  | 'edited'
  | 'published'
  | 'cancelled'
  | 'disabled'
  | 'enabled';

export interface AuditEntry {
  id: string;
  /** Discriminates a creation / lifecycle transition from a plain field edit. */
  eventType: WorkshopAuditEventType;
  actorUserId: string;
  actorEmail: string | null;
  /** Resolved actor name in "First L." form (e.g. "Maya O.").
   *  Falls back to email local-part, then actorUserId. */
  actorName: string;
  /** ISO datetime string (Date serialized over JSON). */
  changedAt: string;
  /** Raw per-field changes as stored, EXCEPT `crew`: its before/after arrays are
   *  resolved server-side from `key|position` tuples to AuditCrewMember[].
   *  Scalar fields (capacity, priceCents, status, lifecycle, …) keep raw
   *  before/after values so the FE formatters render them. */
  fieldChanges: unknown;
}

export interface WorkshopCrewMember {
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  position: 'teacher' | 'assistant' | 'teacher_in_training' | string;
}

/**
 * Workshop publication lifecycle (ADR-0014 / BON-117). A dimension separate
 * from the FareHarbor-mirrored `status`: a `draft` is operator-only and not
 * yet bookable; `active` is the live/published state. `cancelled` is the
 * terminal state produced by the BON-202 cancel flow — an active workshop whose
 * confirmed customers were transferred to another workshop and notified.
 * `disabled` is a published workshop whose new sales are stopped; staff can
 * re-enable it. Mirrors the backend `WORKSHOP_LIFECYCLES`.
 */
export type WorkshopLifecycle = 'draft' | 'active' | 'cancelled' | 'disabled';

/** The workshop's type (BON-117). Species now hangs off the type, not the
 *  workshop directly — `treeSpecies` here is the type's optional species. */
export interface WorkshopTypeSummary {
  id: string;
  name: string;
  displayName: string | null;
  /** The type's marketing description (BON-215). Surfaced on the publish
   *  confirmation modal. Optional/nullable — older responses (and types with no
   *  description set) omit it, so consumers must render it defensively. */
  description?: string | null;
  coveredByTreeGuarantee: boolean;
  treeSpecies: { id: string; name: string } | null;
}

export interface Workshop {
  id: string;
  fareharborItemId: string | null;
  /** Null only for a venueless private event (client site or to be decided). */
  venueId: string | null;
  venueName: string;
  venueCity: string | null;
  venueState: string | null;
  /** The venue's IANA zone, or null when unresolved. Optional: older endpoints omit it. */
  venueTimeZone?: string | null;
  /** True for a private event, which has its own detail page. */
  isPrivate: boolean;
  /** Short venue abbreviation for a dense calendar chip (e.g. "ABS"). Optional:
   *  older endpoints that return `Workshop` don't send it yet. */
  venueShort?: string;
  /** Region the venue belongs to (BON-200). Drives the region-scoped crew
   *  picker. Null when the venue has no region. */
  venueRegionId: string | null;
  venueRegionName: string | null;
  /** Sub-region name for the calendar legend + accent colour, when the venue's
   *  region has one. Optional/nullable — see {@link venueShort}. */
  subregionLabel?: string | null;
  /** The region's calendar-detail-link slug. Optional/nullable — see
   *  {@link venueShort}. */
  regionSlug?: string | null;
  /** The chip's left-accent colour (hex) — the venue's sub-region colour.
   *  Optional — see {@link venueShort}. */
  accentColor?: string;
  /** The crew position the requesting user holds on this workshop, or null
   *  when they aren't on it. */
  myRole?: string | null;
  /** Required FK to the workshop's type (BON-117). */
  workshopTypeId: string;
  /** The workshop's type with its (optional) species. */
  workshopType: WorkshopTypeSummary;
  capacity: number;
  /** Absolute UTC ISO datetime (`Z`-tagged). For sort/compare ONLY — never
   *  displayed. Use `localStartDatetime` for any user-facing time (BON-386). */
  startsAt: string;
  /** Absolute UTC ISO datetime (`Z`-tagged). Sort/compare only (BON-386). */
  endsAt: string;
  /** Venue-local wall clock as a naive ISO string with NO trailing `Z` (e.g.
   *  `2026-08-11T14:00:00`). The ONLY form the FE displays or seeds an edit
   *  form from — never `startsAt`, whose UTC components are not the wall clock
   *  (BON-386, design D10). Format via `utils/venueTime`. `null` only when the
   *  stored `localStartsAt` column is unpopulated. */
  localStartDatetime: string | null;
  /** Venue-local wall clock for the end, naive (no `Z`). See
   *  {@link localStartDatetime}. */
  localEndDatetime: string | null;
  priceCents: number;
  /** Publication lifecycle — drives the header pill (BON-117). */
  lifecycle: WorkshopLifecycle;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  staff: WorkshopCrewMember[];
  /** Sum of attendee_count across active bookings. Drives fill-rate UI.
   *  Per ADR-0010 only workshop attendees count toward capacity — tree
   *  replacements are tracked separately. */
  attendeesBooked: number;
  /** Sum of tree_replacement_count across active bookings — informational. */
  treeReplacementsBooked: number;
  /** Live-derived seats remaining: max(capacity - attendeesBooked, 0). */
  available: number;
  /** Integer 0..100; capped at 100 even if overbooked, so safe to use as
   *  a bar width directly. The "actually overbooked" condition is
   *  `attendeesBooked > capacity`, surfaced as a numeric badge by the
   *  fill-rate component. */
  fillPercent: number;
  // ─── BON-168 extension ──────────────────────────────────────────────────────
  /** Sales-pace bucket for the future-workshop fill column. The BE derives
   *  this from booking velocity vs. days-to-go; the UI maps it to a pill. */
  paceStatus?: WorkshopPaceStatus;
  /** Crew assigned to this workshop, split by position. Distinct from the
   *  legacy `staff` array — `crew` is the BON-168 teacher/assistant contract
   *  the Workshops-tab table renders. The BE resolves each slot to a display
   *  name (or null when unassigned). */
  crew?: WorkshopCrew;
  /** Ops-CONFIRMED fill data sourced from the `workshop_pace` view, coherent
   *  with {@link paceStatus} (so the Fill bar and Status pill never disagree).
   *  `fillPercent` is an integer 0..100, capped even when overbooked. Distinct
   *  from the legacy warehouse-derived top-level `fillPercent`/`attendeesBooked`. */
  opFill?: { bookedSeats: number; capacity: number; fillPercent: number };
}

/** Sales-pace classification surfaced as the Status pill on the venue
 *  Workshops tab (BON-168). */
export type WorkshopPaceStatus = 'on_track' | 'behind_pace' | 'sold_out';

/** Teacher + assistant assigned to a workshop (BON-168 contract). Each slot is
 *  the crew member's resolved display name, or null when unassigned. The BE
 *  derives these from WorkshopStaff rows (position 'teacher' / 'assistant'). */
export interface WorkshopCrew {
  teacher: string | null;
  assistant: string | null;
}

// ─── Functions ────────────────────────────────────────────────────────────────

/** Past/future partition for the list endpoint (BON-197). The SERVER owns the
 *  boundary (a single server-side `now`), so the client never sends `now` for
 *  this list. `direction` wins over `from`/`to`; absent → legacy `from`/`to`. */
export type WorkshopDirection = 'upcoming' | 'past' | 'all';

export interface ListWorkshopsParams {
  venueId?: string;
  /** Region UUID. Backend expands this to the full subtree, so picking
   *  "New York" also returns workshops whose venue is in NYC/Brooklyn/etc. */
  regionId?: string;
  /** First and last dates to include, e.g. "2026-04-01", matched against the
   *  day the workshop starts on its venue's clock. */
  from?: string;
  to?: string;
  /** Past/future partition (BON-197). `upcoming` → startsAt >= now (asc);
   *  `past` → startsAt < now (desc); `all` → no date bound (desc). Wins over
   *  `from`/`to` when both are present. */
  direction?: WorkshopDirection;
  /** Leave cancelled workshops out server-side, before paging (BON-624). */
  excludeCancelled?: boolean;
  cursor?: string;
  limit?: number;
  /** Offset pagination: number of rows to skip from the start of the ordered
   *  result. Mutually exclusive with `cursor` on the backend (cursor wins). */
  offset?: number;
}

/** GET /api/v1/workshops with optional filters. */
export async function fetchWorkshops(params: ListWorkshopsParams = {}): Promise<Workshop[]> {
  const qs = new URLSearchParams();
  // Data-driven so adding a filter doesn't bump cyclomatic complexity.
  const entries: ReadonlyArray<[string, string | number | undefined]> = [
    ['venueId', params.venueId],
    ['regionId', params.regionId],
    ['from', params.from],
    ['to', params.to],
    ['direction', params.direction],
    ['excludeCancelled', params.excludeCancelled ? 'true' : undefined],
    ['cursor', params.cursor],
    ['limit', params.limit],
    ['offset', params.offset],
  ];
  for (const [key, value] of entries) {
    if (value !== undefined) qs.set(key, String(value));
  }

  const query = qs.toString();
  const res = await apiFetch(`/api/v1/workshops${query ? `?${query}` : ''}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch workshops: ${res.status}`);
  }
  const data = (await res.json()) as { workshops: Workshop[] };
  return data.workshops;
}

// ─── Per-booking check-in (BON-204 R2 — partial COUNT) ──────────────────────────

/** Response from `PUT /api/v1/workshops/:workshopId/bookings/:bookingId/check-in`. */
export interface SetCheckInResult {
  bookingId: string;
  /** The booking's resulting checked-in party count (`0..attendeeCount`). */
  checkedInCount: number;
  /** The booking's party size — echoed so the FE renders "{count}/{attendeeCount}". */
  attendeeCount: number;
  /** ISO datetime string of the last update; null when count is 0 (undo). */
  checkedInAt: string | null;
}

/**
 * PUT /api/v1/workshops/:workshopId/bookings/:bookingId/check-in (BON-204 R2).
 *
 * Partial per-booking check-in by COUNT. `checkedInCount > 0` upserts the count
 * (400 if it exceeds the booking's `attendeeCount`; 422 before the gate; 404 if
 * the booking isn't CONFIRMED on the workshop). `checkedInCount === 0` deletes
 * the row (idempotent, allowed anytime; membership-only). Returns the booking's
 * resulting check-in state.
 */
export async function setBookingCheckIn(
  workshopId: string,
  bookingId: string,
  checkedInCount: number,
): Promise<SetCheckInResult> {
  const res = await apiFetch(`/api/v1/workshops/${workshopId}/bookings/${bookingId}/check-in`, {
    method: 'PUT',
    body: JSON.stringify({ checkedInCount }),
  });
  if (!res.ok) await readError(res);
  return res.json();
}

// ─── Workshop detail (BON-117) ─────────────────────────────────────────────────

/** GET /api/v1/workshops/:id → `{ workshop }` with audit + lifecycle. */
export interface WorkshopDetail extends Workshop {
  audit?: AuditEntry[];
  /** the series this workshop came from; null for a one-off. Optional
   *  only as deploy-skew defence — an older API omits it. */
  series?: WorkshopSeriesSummary | null;
  /** Ops-DB bookings roster (BON-199). */
  roster: WorkshopRoster;
  /** BON-478 — staff-created reservations holding seats on this workshop, oldest
   *  first. Empty when none. Their seats are counted in `roster.reservedSeats`
   *  from the same snapshot, so the rows and the bar always agree. */
  reservations: WorkshopReservation[];
  /** BON-238: the requesting user's own crew position on this workshop
   *  (`'teacher'` / `'assistant'` / …), `null` when the viewer isn't assigned. */
  myRole?: string | null;
  /** BON-490 — active tree-replacement pickups, oldest booked first. Optional:
   *  deploy-skew defence (same precedent as `reservations`) — consumer defaults
   *  to `[]`. */
  treeReplacementPickups?: WorkshopTreeReplacement[];
  /** Whether the viewer may add replacements and mark pickups. Absent → no controls. */
  canManageTreeReplacements?: boolean;
}

// ─── Paginated audit log (BON-201) ────────────────────────────────────────────

/** Response from `GET /api/v1/workshops/:id/audit`. */
export interface WorkshopAuditPage {
  events: AuditEntry[];
  /** Total count of edit events for the workshop (independent of page). */
  total: number;
}

export interface FetchWorkshopAuditParams {
  /** Events per page. Default 20, max 100 (server-enforced). */
  limit?: number;
  /** Events to skip from newest (offset pagination). Default 0. */
  offset?: number;
}

/**
 * GET /api/v1/workshops/:id/audit?limit=&offset=
 *
 * Returns enriched `AuditEntry[]` + `total` count, newest-first.
 * Throws (including on 404 when workshop does not exist).
 */
export async function fetchWorkshopAudit(
  id: string,
  params: FetchWorkshopAuditParams = {},
): Promise<WorkshopAuditPage> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const res = await apiFetch(`/api/v1/workshops/${id}/audit${query ? `?${query}` : ''}`);
  if (!res.ok) await readError(res);
  return res.json();
}

export async function fetchWorkshopById(id: string): Promise<WorkshopDetail> {
  const res = await apiFetch(`/api/v1/workshops/${id}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      (data as { error?: string; message?: string }).message ||
        (data as { error?: string }).error ||
        `Failed to fetch workshop: ${res.status}`,
    );
  }
  const body = (await res.json()) as { workshop: WorkshopDetail };
  return body.workshop;
}

/** Fields the detail page can PATCH. `status` is intentionally NOT patched here. */
export type WorkshopPatch = Partial<{
  capacity: number;
  priceCents: number;
  startsAt: string;
  endsAt: string;
  workshopTypeId: string;
}>;

export interface UpdateWorkshopResult {
  workshop: WorkshopDetail;
  /** True iff the patch changed at least one FareHarbor-mirrored field; gates
   *  the post-save reminder modal (mirrors the venue contract). */
  fhMirroredFieldsChanged: boolean;
}

/** Thrown on a 400 validation failure carrying a `fieldErrors` map. */
export class WorkshopValidationError extends Error {
  fieldErrors: Record<string, string>;
  constructor(fieldErrors: Record<string, string>, message = 'Validation failed') {
    super(message);
    this.name = 'WorkshopValidationError';
    this.fieldErrors = fieldErrors;
  }
}

async function readError(res: Response): Promise<never> {
  const err = (await res.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  if (err.fieldErrors) {
    throw new WorkshopValidationError(err.fieldErrors, err.message ?? 'Validation failed');
  }
  throw new Error(err.error || err.message || `Request failed: ${res.status}`);
}

export async function updateWorkshop(
  id: string,
  data: WorkshopPatch,
): Promise<UpdateWorkshopResult> {
  const res = await apiFetch(`/api/v1/workshops/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!res.ok) await readError(res);
  return res.json();
}

// ─── Create (BON-203) ──────────────────────────────────────────────────────────

/**
 * Create-workshop payload (BON-203). Mirrors the backend `WorkshopCreateSchema`.
 * The server forces `lifecycle='draft'` and `status=null`, so neither is
 * accepted here. `teacherUserId` is optional → one `WorkshopStaff` row with
 * `position='teacher'`.
 *
 * With `recurrence` the same workshop is created on that cadence
 * until the end condition, every occurrence its own draft; the response is
 * the first one, whose `series.total` says how many were made.
 */
export interface WorkshopCreateInput {
  venueId: string;
  workshopTypeId: string;
  /** ISO datetime string. */
  startsAt: string;
  /** ISO datetime string; must be strictly after `startsAt`. */
  endsAt: string;
  capacity: number;
  priceCents: number;
  /** Optional teacher → WorkshopStaff(position='teacher'). */
  teacherUserId?: string;
  /** Repeat on a cadence; the weekday comes from `startsAt`. `occurrenceStarts`
   *  lists every workshop's start when the operator changed any date by hand;
   *  without it the API generates the dates from the cadence. */
  recurrence?: WorkshopRecurrence & { occurrenceStarts?: string[] };
}

/**
 * POST /api/v1/workshops — create a workshop as a draft (BON-203), or a series
 * of drafts with `recurrence`. Returns the full `WorkshopDetail`
 * (same shape as `GET /workshops/:id`) so the caller can navigate straight to
 * the detail page — for a series, the first occurrence. Throws
 * {@link WorkshopValidationError} on a 400 carrying `fieldErrors` (the series
 * rules report under `recurrence`).
 */
export async function createWorkshop(input: WorkshopCreateInput): Promise<WorkshopDetail> {
  const res = await apiFetch('/api/v1/workshops', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) await readError(res);
  const body = (await res.json()) as { workshop: WorkshopDetail };
  return body.workshop;
}

// ─── Crew replacement (BON-200) ────────────────────────────────────────────────

/** A crew slot's position — the shared canonical list (BON-452), re-exported
 *  here because this module is where the rest of the app imports it from. */
export type { CrewPosition };

/** One desired crew slot in a `PUT /:id/staff` body (BON-200). */
export interface CrewSlotInput {
  userId: string;
  position: CrewPosition;
}

/**
 * PUT /api/v1/workshops/:id/staff — FULL replacement of the workshop's crew
 * bridge (BON-200). The body is the complete desired set; the backend writes a
 * single `crew` audit event. Returns the refreshed workshop.
 */
export async function replaceWorkshopStaff(
  id: string,
  staff: CrewSlotInput[],
): Promise<UpdateWorkshopResult> {
  const res = await apiFetch(`/api/v1/workshops/${id}/staff`, {
    method: 'PUT',
    body: JSON.stringify({ staff }),
  });
  if (!res.ok) await readError(res);
  return res.json();
}

/** POST /api/v1/workshops/:id/publish — draft → active. */
export async function publishWorkshop(id: string): Promise<WorkshopDetail> {
  const res = await apiFetch(`/api/v1/workshops/${id}/publish`, { method: 'POST' });
  if (!res.ok) await readError(res);
  const body = (await res.json()) as { workshop: WorkshopDetail };
  return body.workshop;
}

/** DELETE /api/v1/workshops/:id — discard a draft (zero bookings only). */
export async function discardWorkshop(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/workshops/${id}`, { method: 'DELETE' });
  if (!res.ok) await readError(res);
}

// ─── Cancel + rebook (BON-202) ─────────────────────────────────────────────────
// Implemented in `workshopsCancel.ts` (keeps this module under the line cap);
// re-exported here so callers keep importing from `../api/workshops`.
export { cancelWorkshop, CancelWorkshopError } from './workshopsCancel';
export type {
  CancelWorkshopBody,
  CancelWorkshopErrorCode,
  CancelWorkshopResult,
} from './workshopsCancel';
